import { AuthError } from "@supabase/supabase-js";

/**
 * Auth Error Handler
 *
 * Provides user-friendly error messages based on specific Supabase auth error codes
 * instead of generic error messages. This improves UX by giving users clear,
 * actionable feedback about what went wrong during authentication.
 */

export interface AuthErrorInfo {
  title: string;
  message: string;
  actionable: boolean; // Whether the user can take action to fix this
  suggestedAction?: string;
}

export interface AuthErrorHandlerResult {
  title: string;
  description: string;
  variant: "destructive" | "default";
  actionable: boolean;
  suggestedAction?: string;
}

/**
 * Get user-friendly error information based on Supabase auth error codes
 */
export function getAuthErrorInfo(error: AuthError | Error): AuthErrorInfo {
  // Check if it's an AuthError with a code property
  const errorCode = (error as any)?.code;
  const errorMessage = error.message;

  // Handle specific Supabase auth error codes
  switch (errorCode) {
    // Login/Credential Errors
    case "invalid_credentials":
      return {
        title: "Invalid credentials",
        message:
          "The email or password you entered is incorrect. Please check your credentials and try again.",
        actionable: true,
        suggestedAction:
          "Double-check your email and password, or use 'Forgot Password' if needed.",
      };

    // Obsolete: user_not_found -> mapped to identity_not_found or invalid_credentials context
    case "user_not_found":
    case "identity_not_found":
      return {
        title: "Account not found",
        message:
          "We couldn't find an account with that email address. Please check the email or sign up for a new account.",
        actionable: true,
        suggestedAction: "Verify the email address or create a new account.",
      };

    case "email_not_confirmed":
      return {
        title: "Email not verified",
        message:
          "Please check your email and click the verification link before signing in.",
        actionable: true,
        suggestedAction:
          "Check your email inbox (and spam folder) for the verification email.",
      };

    // Registration Errors
    case "email_exists":
    case "user_already_exists":
      return {
        title: "Account already exists",
        message:
          "An account with this email address already exists. Please sign in instead or use a different email.",
        actionable: true,
        suggestedAction:
          "Try signing in or use the 'Forgot Password' option if you can't remember your password.",
      };

    case "signup_disabled":
      return {
        title: "Registration disabled",
        message:
          "New account registration is currently disabled. Please contact support for assistance.",
        actionable: false,
      };

    // Password Errors
    case "weak_password":
      return {
        title: "Password too weak",
        message:
          "Your password doesn't meet our security requirements. Please choose a stronger password.",
        actionable: true,
        suggestedAction:
          "Use at least 8 characters with uppercase, lowercase, numbers, and special characters.",
      };

    case "same_password":
      return {
        title: "Password unchanged",
        message:
          "Your new password must be different from your current password.",
        actionable: true,
        suggestedAction: "Choose a completely different password.",
      };

    // Rate Limiting Errors
    case "over_email_send_rate_limit":
      return {
        title: "Too many emails sent",
        message:
          "We've sent too many emails to this address recently. Please wait a few minutes before trying again.",
        actionable: true,
        suggestedAction: "Wait 5-10 minutes before requesting another email.",
      };

    case "over_request_rate_limit":
      return {
        title: "Too many attempts",
        message:
          "Too many login attempts. Please wait a few minutes before trying again.",
        actionable: true,
        suggestedAction:
          "Wait 5-10 minutes before attempting to sign in again.",
      };

    case "over_sms_send_rate_limit":
      return {
        title: "Too many SMS messages",
        message:
          "We've sent too many SMS messages to this number recently. Please wait before trying again.",
        actionable: true,
        suggestedAction: "Wait 5-10 minutes before requesting another SMS.",
      };

    // Phone-specific Errors
    case "phone_exists":
      return {
        title: "Phone number already registered",
        message:
          "This phone number is already associated with an account. Please sign in instead.",
        actionable: true,
        suggestedAction: "Try signing in with this phone number.",
      };

    case "phone_not_confirmed":
      return {
        title: "Phone not verified",
        message: "Please verify your phone number before signing in.",
        actionable: true,
        suggestedAction:
          "Check for SMS verification code and enter it to verify your phone.",
      };

    // Provider Errors
    case "email_provider_disabled":
      return {
        title: "Email sign-in disabled",
        message:
          "Email and password sign-in is currently disabled. Please try alternative sign-in methods.",
        actionable: false,
      };

    case "phone_provider_disabled":
      return {
        title: "Phone sign-in disabled",
        message:
          "Phone number sign-in is currently disabled. Please try alternative sign-in methods.",
        actionable: false,
      };

    // Session/Token Errors
    case "session_not_found":
    case "session_expired":
      return {
        title: "Session expired",
        message: "Your session has expired. Please sign in again.",
        actionable: true,
        suggestedAction: "Sign in again to continue.",
      };

    case "refresh_token_not_found":
    case "refresh_token_already_used":
      return {
        title: "Authentication expired",
        message: "Your authentication has expired. Please sign in again.",
        actionable: true,
        suggestedAction: "Sign in again to continue.",
      };

    // OTP/Magic Link Errors
    case "otp_expired":
      return {
        title: "Verification code expired",
        message: "The verification code has expired. Please request a new one.",
        actionable: true,
        suggestedAction: "Request a new verification code.",
      };

    case "otp_disabled":
      return {
        title: "OTP sign-in disabled",
        message: "One-time password sign-in is currently disabled.",
        actionable: false,
      };

    // Invite Errors
    case "invite_not_found":
      return {
        title: "Invalid invitation",
        message: "This invitation link is invalid or has already been used.",
        actionable: false,
      };

    // CAPTCHA Errors
    case "captcha_failed":
      return {
        title: "CAPTCHA verification failed",
        message: "Please complete the CAPTCHA verification and try again.",
        actionable: true,
        suggestedAction: "Complete the CAPTCHA challenge.",
      };

    // General Server Errors
    case "unexpected_failure":
      return {
        title: "Service temporarily unavailable",
        message:
          "Our authentication service is experiencing issues. Please try again in a few moments.",
        actionable: true,
        suggestedAction: "Try again in a few minutes.",
      };

    // New Supabase Auth Error Codes (2024+)
    case "anonymous_provider_disabled":
      return {
        title: "Anonymous sign-in disabled",
        message:
          "Anonymous sign-ins are currently disabled for this application.",
        actionable: false,
      };

    case "bad_code_verifier":
      return {
        title: "Authentication error",
        message:
          "There was an issue with the authentication process. Please try signing in again.",
        actionable: true,
        suggestedAction: "Start a new sign-in attempt.",
      };

    case "bad_json":
      return {
        title: "Request error",
        message:
          "There was an issue processing your request. Please try again.",
        actionable: true,
        suggestedAction: "Refresh the page and try again.",
      };

    case "bad_jwt":
      return {
        title: "Session expired",
        message:
          "Your authentication session is invalid. Please sign in again.",
        actionable: true,
        suggestedAction: "Sign in again to continue.",
      };

    case "bad_oauth_callback":
    case "bad_oauth_state":
      return {
        title: "OAuth authentication failed",
        message: "There was an issue with social sign-in. Please try again.",
        actionable: true,
        suggestedAction: "Try signing in again with your chosen provider.",
      };

    case "conflict":
      return {
        title: "Request conflict",
        message:
          "Multiple requests are conflicting. Please wait a moment and try again.",
        actionable: true,
        suggestedAction: "Wait a few seconds and try again.",
      };

    case "email_address_invalid":
      return {
        title: "Invalid email address",
        message:
          "The email address format is invalid. Please use a different email address.",
        actionable: true,
        suggestedAction: "Enter a valid email address.",
      };

    case "email_address_not_authorized":
      return {
        title: "Email not authorized",
        message:
          "This email address is not authorized. Please contact support or use a custom SMTP provider.",
        actionable: false,
      };

    case "email_conflict_identity_not_deletable":
      return {
        title: "Email conflict",
        message:
          "Cannot unlink this identity as it would create an email conflict with another account.",
        actionable: false,
      };

    case "flow_state_expired":
    case "flow_state_not_found":
      return {
        title: "Authentication expired",
        message:
          "Your authentication session has expired. Please sign in again.",
        actionable: true,
        suggestedAction: "Start a new sign-in attempt.",
      };

    case "hook_payload_invalid_content_type":
    case "hook_payload_over_size_limit":
    case "hook_timeout":
    case "hook_timeout_after_retry":
      return {
        title: "Service configuration error",
        message:
          "There's an issue with our authentication service. Please contact support.",
        actionable: false,
      };

    case "identity_already_exists":
      return {
        title: "Identity already linked",
        message: "This identity is already linked to another account.",
        actionable: false,
      };

    case "insufficient_aal":
      return {
        title: "Additional verification required",
        message:
          "This action requires multi-factor authentication. Please complete MFA verification.",
        actionable: true,
        suggestedAction: "Complete the MFA challenge to continue.",
      };

    case "manual_linking_disabled":
      return {
        title: "Account linking disabled",
        message: "Manual account linking is not enabled for this application.",
        actionable: false,
      };

    // MFA (Multi-Factor Authentication) Errors
    case "mfa_challenge_expired":
      return {
        title: "MFA challenge expired",
        message:
          "The multi-factor authentication challenge has expired. Please request a new one.",
        actionable: true,
        suggestedAction: "Request a new MFA challenge.",
      };

    case "mfa_factor_name_conflict":
      return {
        title: "MFA factor name conflict",
        message:
          "You already have an MFA factor with this name. Please choose a different name.",
        actionable: true,
        suggestedAction: "Use a different name for your MFA factor.",
      };

    case "mfa_factor_not_found":
      return {
        title: "MFA factor not found",
        message: "The requested MFA factor no longer exists.",
        actionable: false,
      };

    case "mfa_ip_address_mismatch":
      return {
        title: "MFA enrollment error",
        message: "MFA enrollment must be completed from the same IP address.",
        actionable: true,
        suggestedAction:
          "Complete MFA enrollment from the same network connection.",
      };

    case "mfa_phone_enroll_not_enabled":
      return {
        title: "Phone MFA not available",
        message:
          "Phone-based multi-factor authentication enrollment is not enabled.",
        actionable: false,
      };

    case "mfa_phone_verify_not_enabled":
      return {
        title: "Phone MFA verification disabled",
        message:
          "Phone-based multi-factor authentication verification is not enabled.",
        actionable: false,
      };

    case "mfa_totp_enroll_not_enabled":
      return {
        title: "TOTP MFA not available",
        message:
          "TOTP-based multi-factor authentication enrollment is not enabled.",
        actionable: false,
      };

    case "mfa_totp_verify_not_enabled":
      return {
        title: "TOTP MFA verification disabled",
        message:
          "TOTP-based multi-factor authentication verification is not enabled.",
        actionable: false,
      };

    case "mfa_verification_failed":
      return {
        title: "MFA verification failed",
        message:
          "The multi-factor authentication code is incorrect. Please try again.",
        actionable: true,
        suggestedAction:
          "Enter the correct MFA code from your authenticator app.",
      };

    case "mfa_verification_rejected":
      return {
        title: "MFA verification rejected",
        message: "Multi-factor authentication verification was rejected.",
        actionable: false,
      };

    case "mfa_verified_factor_exists":
      return {
        title: "MFA factor already exists",
        message:
          "You already have a verified phone factor. Remove it before adding a new one.",
        actionable: true,
        suggestedAction: "Remove your existing phone factor first.",
      };

    case "mfa_web_authn_enroll_not_enabled":
      return {
        title: "WebAuthn MFA not available",
        message:
          "WebAuthn-based multi-factor authentication enrollment is not enabled.",
        actionable: false,
      };

    case "mfa_web_authn_verify_not_enabled":
      return {
        title: "WebAuthn MFA verification disabled",
        message:
          "WebAuthn-based multi-factor authentication verification is not enabled.",
        actionable: false,
      };

    case "no_authorization":
      return {
        title: "Authorization required",
        message:
          "This request requires authorization. Please sign in and try again.",
        actionable: true,
        suggestedAction: "Sign in to access this resource.",
      };

    case "not_admin":
      return {
        title: "Admin access required",
        message: "This action requires administrator privileges.",
        actionable: false,
      };

    case "oauth_provider_not_supported":
      return {
        title: "OAuth provider not supported",
        message:
          "The selected OAuth provider is not enabled for this application.",
        actionable: false,
      };

    case "provider_disabled":
      return {
        title: "Sign-in provider disabled",
        message: "The selected sign-in provider is currently disabled.",
        actionable: false,
      };

    case "provider_email_needs_verification":
      return {
        title: "Email verification required",
        message:
          "Please check your email and verify your email address before continuing.",
        actionable: true,
        suggestedAction: "Check your email and click the verification link.",
      };

    case "reauthentication_needed":
      return {
        title: "Re-authentication required",
        message:
          "Please re-authenticate to change your password for security reasons.",
        actionable: true,
        suggestedAction: "Complete the re-authentication process.",
      };

    case "reauthentication_not_valid":
      return {
        title: "Re-authentication failed",
        message: "The re-authentication code is incorrect. Please try again.",
        actionable: true,
        suggestedAction: "Enter the correct re-authentication code.",
      };

    case "request_timeout":
      return {
        title: "Request timeout",
        message: "The request took too long to process. Please try again.",
        actionable: true,
        suggestedAction: "Try your request again.",
      };

    // SAML (Enterprise SSO) Errors
    case "saml_assertion_no_email":
      return {
        title: "SAML configuration error",
        message:
          "No email address found in SAML response. Please contact your administrator.",
        actionable: false,
      };

    case "saml_assertion_no_user_id":
      return {
        title: "SAML configuration error",
        message:
          "No user ID found in SAML response. Please contact your administrator.",
        actionable: false,
      };

    case "saml_entity_id_mismatch":
      return {
        title: "SAML entity ID mismatch",
        message:
          "SAML identity provider configuration error. Please contact support.",
        actionable: false,
      };

    case "saml_idp_already_exists":
      return {
        title: "SAML provider already exists",
        message: "This SAML identity provider is already configured.",
        actionable: false,
      };

    case "saml_idp_not_found":
      return {
        title: "SAML provider not found",
        message:
          "The SAML identity provider is not configured for this application.",
        actionable: false,
      };

    case "saml_metadata_fetch_failed":
      return {
        title: "SAML metadata error",
        message:
          "Failed to fetch SAML metadata. Please contact your administrator.",
        actionable: false,
      };

    case "saml_provider_disabled":
      return {
        title: "SAML sign-in disabled",
        message:
          "Enterprise SSO with SAML is not enabled for this application.",
        actionable: false,
      };

    case "saml_relay_state_expired":
    case "saml_relay_state_not_found":
      return {
        title: "SAML authentication expired",
        message:
          "Your SAML authentication session has expired. Please sign in again.",
        actionable: true,
        suggestedAction: "Start a new SSO sign-in attempt.",
      };

    case "single_identity_not_deletable":
      return {
        title: "Cannot remove identity",
        message:
          "You cannot remove your only remaining identity. Add another sign-in method first.",
        actionable: true,
        suggestedAction: "Add another sign-in method before removing this one.",
      };

    case "sms_send_failed":
      return {
        title: "SMS sending failed",
        message:
          "Failed to send SMS message. Please try again or use a different method.",
        actionable: true,
        suggestedAction: "Try again or use email authentication instead.",
      };

    case "sso_domain_already_exists":
      return {
        title: "SSO domain already exists",
        message:
          "This domain is already configured for SSO with another provider.",
        actionable: false,
      };

    case "sso_provider_not_found":
      return {
        title: "SSO provider not found",
        message: "The specified SSO provider is not configured.",
        actionable: false,
      };

    case "too_many_enrolled_mfa_factors":
      return {
        title: "Too many MFA factors",
        message:
          "You have reached the maximum number of MFA factors. Remove one before adding another.",
        actionable: true,
        suggestedAction: "Remove an existing MFA factor first.",
      };

    case "user_banned":
      return {
        title: "Account suspended",
        message:
          "Your account has been suspended. Please contact support for assistance.",
        actionable: false,
      };

    case "user_sso_managed":
      return {
        title: "SSO-managed account",
        message:
          "This account is managed by your organization's SSO. Some settings cannot be changed.",
        actionable: false,
      };

    case "validation_failed":
      return {
        title: "Validation failed",
        message:
          "The provided information is not in the correct format. Please check and try again.",
        actionable: true,
        suggestedAction:
          "Review the form fields and ensure all information is correct.",
      };

    // Network/Connection Errors
    default:
      // Handle common error message patterns for network issues
      if (
        errorMessage?.toLowerCase().includes("network") ||
        errorMessage?.toLowerCase().includes("fetch") ||
        errorMessage?.toLowerCase().includes("timeout")
      ) {
        return {
          title: "Connection error",
          message:
            "Unable to connect to our servers. Please check your internet connection and try again.",
          actionable: true,
          suggestedAction: "Check your internet connection and try again.",
        };
      }

      // Handle any other validation errors
      if (
        errorCode === "validation_failed" ||
        errorMessage?.toLowerCase().includes("validation")
      ) {
        return {
          title: "Invalid information",
          message:
            "Please check that all fields are filled out correctly and try again.",
          actionable: true,
          suggestedAction:
            "Review the form fields and ensure all information is correct.",
        };
      }

      // Fallback for unknown errors - but still show the actual error message
      return {
        title: "Authentication error",
        message:
          errorMessage ||
          "An unexpected error occurred during authentication. Please try again.",
        actionable: true,
        suggestedAction:
          "Try again, or contact support if the problem persists.",
      };
  }
}

/**
 * Get formatted error information ready for toast notifications
 */
export function formatAuthError(
  error: AuthError | Error,
): AuthErrorHandlerResult {
  const errorInfo = getAuthErrorInfo(error);

  return {
    title: errorInfo.title,
    description: errorInfo.message,
    variant: "destructive" as const,
    actionable: errorInfo.actionable,
    suggestedAction: errorInfo.suggestedAction,
  };
}

/**
 * Enhanced error logging for debugging
 */
export function logAuthError(error: AuthError | Error, context: string) {
  const errorCode = (error as any)?.code;
  const errorStatus = (error as any)?.status;

  console.group(`[Auth Error] ${context}`);
  console.error("Error object:", error);
  console.log("Error code:", errorCode);
  console.log("HTTP status:", errorStatus);
  console.log("Error message:", error.message);
  console.log("User-friendly info:", getAuthErrorInfo(error));
  console.groupEnd();
}

/**
 * Check if an error is recoverable by user action
 */
export function isRecoverableAuthError(error: AuthError | Error): boolean {
  const errorInfo = getAuthErrorInfo(error);
  return errorInfo.actionable;
}

/**
 * Get suggested action for an error (if any)
 */
export function getAuthErrorAction(
  error: AuthError | Error,
): string | undefined {
  const errorInfo = getAuthErrorInfo(error);
  return errorInfo.suggestedAction;
}
